import { motion } from "framer-motion";

/* Decorative pseudo-3D layer — replaces previous confetti/lantern style with
 * organic gold vines, leaves, tendrils and starburst flourishes inspired by the
 * Shree Event Planner logo (curving flowing lines + small oval leaves + curling
 * tendrils + starburst). Indian luxury botanical aesthetic. */

export default function FloatingDecor({ scrollY = 0 }) {
  return (
    <div className="pointer-events-none fixed inset-0 z-[2]" aria-hidden>
      {/* Soft global Indian warm gradients */}
      <div
        className="absolute inset-0 opacity-70"
        style={{
          background:
            "radial-gradient(circle at 12% 8%, rgba(212,175,55,0.07), transparent 35%), radial-gradient(circle at 88% 60%, rgba(201,48,46,0.05), transparent 40%), radial-gradient(circle at 50% 90%, rgba(224,138,31,0.04), transparent 40%)",
        }}
      />

      {/* TOP: hanging vine flourishes */}
      <HangingVine x="6%" delay={0} duration={9} />
      <HangingVine x="28%" delay={1.2} duration={11} flip />
      <HangingVine x="54%" delay={0.4} duration={10} />
      <HangingVine x="76%" delay={1.8} duration={8} flip />
      <HangingVine x="94%" delay={0.6} duration={12} />

      {/* SIDE: floating tendrils / swirls */}
      <FloatingSwirl style={{ top: "20%", left: "3%" }} delay={0} scale={0.9} />
      <FloatingSwirl style={{ top: "55%", right: "3%" }} delay={1.5} scale={1.1} flip />
      <FloatingSwirl style={{ bottom: "10%", left: "8%" }} delay={2.2} scale={0.7} />
      <FloatingSwirl style={{ top: "38%", right: "12%" }} delay={3.0} scale={0.6} flip />

      {/* Drifting leaves */}
      {LEAVES.map((l) => (
        <DriftingLeaf key={l.id} {...l} scrollY={scrollY} />
      ))}

      {/* Starburst flourishes */}
      {STARS.map((s) => (
        <PulseStar key={s.id} {...s} />
      ))}

      {/* tiny shimmer dots */}
      {DOTS.map((d) => (
        <motion.div
          key={`dot-${d.id}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.65, 0], y: [-5, 12, -5] }}
          transition={{ duration: d.duration, delay: d.delay, repeat: Infinity, ease: "easeInOut" }}
          className="absolute rounded-full"
          style={{
            left: `${d.left}%`,
            top: `${d.top}%`,
            width: d.size,
            height: d.size,
            background: d.color,
            boxShadow: `0 0 10px ${d.color}`,
            transform: `translateY(${scrollY * 0.02}px)`,
          }}
        />
      ))}
    </div>
  );
}

/* ============== components ============== */

function HangingVine({ x, delay = 0, duration = 10, flip = false }) {
  return (
    <motion.div
      className="absolute top-0 origin-top"
      style={{ left: x, transform: flip ? "scaleX(-1)" : undefined }}
      animate={{ rotate: [-2.5, 2.5, -2.5] }}
      transition={{ duration, repeat: Infinity, ease: "easeInOut", delay }}
    >
      <svg width="80" height="240" viewBox="0 0 80 240" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="vineG" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0" />
            <stop offset="20%" stopColor="#D4AF37" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#E8C988" stopOpacity="0.65" />
          </linearGradient>
        </defs>
        <g stroke="url(#vineG)" strokeWidth="1.2" fill="none" strokeLinecap="round">
          {/* main hanging vine */}
          <path d="M40 0 C 36 30, 44 60, 38 95 S 32 150, 40 180 C 44 195, 36 210, 42 225" />
          {/* tendril curls */}
          <path d="M38 70 c -6 4, -10 -1, -8 -7 c 2 -6, 9 -5, 10 0" />
          <path d="M42 130 c 7 3, 11 -2, 8 -8 c -3 -5, -10 -3, -9 2" />
          <path d="M40 200 c -8 2, -12 -4, -8 -10 c 4 -6, 11 -2, 10 4" />
        </g>
        {/* leaves along the vine */}
        <g fill="#D4AF37" opacity="0.8">
          <ellipse cx="32" cy="55" rx="3.5" ry="6" transform="rotate(-30 32 55)" />
          <ellipse cx="46" cy="90" rx="3" ry="5.5" transform="rotate(35 46 90)" />
          <ellipse cx="34" cy="120" rx="3.5" ry="6" transform="rotate(-20 34 120)" />
          <ellipse cx="48" cy="165" rx="3" ry="5" transform="rotate(40 48 165)" />
          <ellipse cx="32" cy="210" rx="3" ry="5.5" transform="rotate(-25 32 210)" />
        </g>
      </svg>
    </motion.div>
  );
}

function FloatingSwirl({ style, delay = 0, scale = 1, flip = false }) {
  return (
    <motion.div
      className="absolute hidden md:block"
      style={{
        ...style,
        transform: `scale(${scale}) ${flip ? "scaleX(-1)" : ""}`,
        opacity: 0.55,
      }}
      animate={{
        rotate: [0, 8, -4, 0],
        y: [0, -12, 6, 0],
      }}
      transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay }}
    >
      <svg width="130" height="90" viewBox="0 0 130 90" xmlns="http://www.w3.org/2000/svg">
        <g stroke="#D4AF37" strokeWidth="1" fill="none" strokeLinecap="round">
          {/* main scroll */}
          <path d="M5 45 C 25 10, 70 5, 95 30 C 110 45, 105 65, 88 70 C 75 73, 68 60, 78 52 C 86 46, 92 52, 90 60" />
          {/* secondary tendril */}
          <path d="M40 38 c -4 8, 4 18, 14 14 c 8 -3, 6 -14, -2 -14" />
          {/* leaves */}
          <ellipse cx="55" cy="20" rx="2.5" ry="5" transform="rotate(-30 55 20)" fill="#D4AF37" stroke="none" />
          <ellipse cx="78" cy="14" rx="2.5" ry="5" transform="rotate(20 78 14)" fill="#D4AF37" stroke="none" />
          <ellipse cx="30" cy="58" rx="2.5" ry="5" transform="rotate(-40 30 58)" fill="#D4AF37" stroke="none" />
          {/* tiny dot at curl */}
          <circle cx="90" cy="60" r="1.6" fill="#E8C988" stroke="none" />
        </g>
      </svg>
    </motion.div>
  );
}

function DriftingLeaf({ left, top, delay, duration, rotate, size, scrollY }) {
  return (
    <motion.div
      className="absolute"
      style={{
        left: `${left}%`,
        top: `${top}%`,
        transform: `translateY(${scrollY * 0.04}px)`,
      }}
      animate={{
        y: [0, -25, 0, 18, 0],
        x: [0, 14, -8, 6, 0],
        rotate: [rotate, rotate + 20, rotate - 15, rotate + 8, rotate],
      }}
      transition={{ duration, delay, repeat: Infinity, ease: "easeInOut" }}
    >
      <svg width={size} height={size * 1.5} viewBox="0 0 20 30" xmlns="http://www.w3.org/2000/svg">
        <g fill="#D4AF37" opacity="0.55">
          <ellipse cx="10" cy="15" rx="3.5" ry="9" />
          <path d="M10 6 L 10 24" stroke="#8E6B1A" strokeWidth="0.4" opacity="0.6" />
        </g>
      </svg>
    </motion.div>
  );
}

function PulseStar({ left, top, delay, size = 14 }) {
  return (
    <motion.div
      className="absolute"
      style={{ left: `${left}%`, top: `${top}%` }}
      animate={{ scale: [0.6, 1.1, 0.6], opacity: [0.25, 0.85, 0.25], rotate: [0, 60] }}
      transition={{ duration: 5, delay, repeat: Infinity, ease: "easeInOut" }}
    >
      <svg width={size} height={size} viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
        <g fill="#E8C988" opacity="0.9">
          <path d="M15 2 L 17 13 L 28 15 L 17 17 L 15 28 L 13 17 L 2 15 L 13 13 Z" />
        </g>
      </svg>
    </motion.div>
  );
}

/* ============== data ============== */

const LEAVES = Array.from({ length: 14 }).map((_, i) => ({
  id: i,
  left: (i * 71) % 100,
  top: 10 + ((i * 47) % 80),
  delay: (i % 7) * 0.7,
  duration: 9 + (i % 5),
  rotate: (i * 37) % 360,
  size: 10 + (i % 4) * 2,
}));

const STARS = [
  { id: "s1", left: 18, top: 22, delay: 0 },
  { id: "s2", left: 78, top: 18, delay: 1.2, size: 12 },
  { id: "s3", left: 42, top: 70, delay: 0.6, size: 18 },
  { id: "s4", left: 88, top: 78, delay: 2.0 },
  { id: "s5", left: 8, top: 64, delay: 2.4, size: 10 },
  { id: "s6", left: 60, top: 40, delay: 1.6, size: 12 },
];

const DOTS = Array.from({ length: 14 }).map((_, i) => ({
  id: i,
  left: (i * 41) % 100,
  top: 5 + ((i * 29) % 90),
  size: 3 + (i % 3),
  color: i % 3 === 0 ? "#D4AF37" : i % 3 === 1 ? "#E08A1F" : "#F3E5AB",
  delay: (i % 5) * 0.8,
  duration: 5 + (i % 4),
}));
